import {useState, useEffect} from 'react';
import {getDemoVideoStoragePath} from './uploadVideo';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import type {UserId} from 'types/common';

/** Loaded teaching-demo Storage state retained by the hook. */
export interface UploadedVideoServerData {
  /** Download URL for the current object, or null before one is loaded. */
  videoUrl: string | null;
  /** Storage object generation when callers need upload identity. */
  generation?: string | null;
  /** Browser playback type reported by Storage metadata. */
  contentType?: string | null;
  /** Non-not-found read failure, or null for an ordinary missing upload. */
  error: Error | null;
}

/** Playable teaching-demo object metadata used by review surfaces. */
export interface UploadedVideoSource {
  /** Download URL for the current object. */
  videoUrl: string;
  /** Storage object generation that uniquely identifies this upload. */
  generation: string;
  /** Browser playback type reported by Storage metadata. */
  contentType: string | null;
}

/**
 * Loads the current teaching-demo object and its upload identity.
 *
 * @param userId - Applicant whose single-file demo should be loaded.
 * @returns URL, loading state, error, content type, and Storage generation.
 * @throws Never. Read failures are returned in the tuple.
 */
export const useUploadedVideo = (
  userId: UserId | null | undefined,
): [string | null, boolean, Error | null, string | null, string | null] => {
  const [result, setResult] = useState<
    UploadedVideoServerData & {userId: UserId | null}
  >({
    userId: null,
    videoUrl: null,
    generation: null,
    contentType: null,
    error: null,
  });

  useEffect(() => {
    if (!userId) {
      return;
    }

    let isCancelled = false;
    getSectionLeaderDemoVideoSource(userId)
      .then(({videoUrl, contentType, generation}) => {
        if (isCancelled) {
          return;
        }
        setResult({userId, videoUrl, contentType, generation, error: null});
      })
      .catch((error: unknown) => {
        if (isCancelled) {
          return;
        }
        // No uploaded video is an ordinary state, not a failure. Applicants
        // who have not recorded yet, and applicants who only did the live
        // teaching exercise, simply have no object at this path.
        const isNotFound =
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          error.code === 'storage/object-not-found';
        const normalizedError =
          error instanceof Error ? error : new Error(String(error));
        setResult({
          userId,
          videoUrl: null,
          generation: null,
          contentType: null,
          error: isNotFound ? null : normalizedError,
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [userId]);

  const isCurrentResult = Boolean(userId) && result.userId === userId;
  const videoUrl = isCurrentResult ? result.videoUrl : null;
  const error = isCurrentResult ? result.error : null;
  const contentType = isCurrentResult ? (result.contentType ?? null) : null;
  const generation = isCurrentResult ? result.generation : null;
  const isLoading = Boolean(userId) && !isCurrentResult;

  return [videoUrl, isLoading, error, contentType, generation];
};

/**
 * Gets the URL of the section leader demo video for a given user.
 *
 * @throws if the video is not found
 */
export const getSectionLeaderDemoVideoUrl = async (
  userId: UserId,
): Promise<string> => {
  const source = await getSectionLeaderDemoVideoSource(userId);
  return source.videoUrl;
};

/**
 * Gets the URL, upload generation, and content type of a teaching demo.
 *
 * @param userId - Applicant whose current demo object should be loaded.
 * @returns Playable URL and Storage metadata for that exact object generation.
 * @throws If the object cannot be read.
 */
export const getSectionLeaderDemoVideoSource = async (
  userId: UserId,
): Promise<UploadedVideoSource> => {
  const storage = firebase.storage();
  const storageRef = storage.ref();
  const path = getDemoVideoStoragePath(userId);
  const videoRef = storageRef.child(path);
  const [videoUrl, metadata] = await Promise.all([
    videoRef.getDownloadURL(),
    videoRef.getMetadata(),
  ]);

  return {
    videoUrl,
    generation: metadata.generation,
    contentType: metadata.contentType ?? null,
  };
};
